import { c as _c } from "react/compiler-runtime";
import { useActionsContext, useFiltersContext, usePanelContext } from "../context";
import { DATE_TERMS_FOR_SCORING, getFilterIcon, tryParseDate } from "../helpers";
import { getStrategy } from "../strategies";
import type { SearchContext } from "../strategies";
import type { ComparisonType, FilterTypeConfig, SearchResult } from "../types";
import { useDeferredValue, useMemo } from "react";
import { RiCheckLine } from "react-icons/ri";
import { DropdownMenuItem } from "@/components/ui/dropdown";
import { useProviders } from "@/hooks/use-providers";
import { cx } from "@/lib/tailwind-utils";
function buildSearchContext(searchQuery: string, enhancedFilterTypes: FilterTypeConfig[], providers: ReturnType<typeof useProviders>["providers"]): SearchContext {
  const trimmedQuery = searchQuery.trim();
  const query = trimmedQuery.toLowerCase();
  let parsedNumberQuery: SearchContext["parsedNumberQuery"] = null;
  if (trimmedQuery) {
    const comparisonPatterns: {
      pattern: RegExp;
      comparison: ComparisonType;
    }[] = [{
      pattern: /^>=?\s*(-?\d+\.?\d*)$/,
      comparison: "gte"
    }, {
      pattern: /^≥\s*(-?\d+\.?\d*)$/,
      comparison: "gte"
    }, {
      pattern: /^<=?\s*(-?\d+\.?\d*)$/,
      comparison: "lte"
    }, {
      pattern: /^≤\s*(-?\d+\.?\d*)$/,
      comparison: "lte"
    }, {
      pattern: /^=\s*(-?\d+\.?\d*)$/,
      comparison: "eq"
    }];
    for (const {
      pattern,
      comparison
    } of comparisonPatterns) {
      const match = trimmedQuery.match(pattern);
      if (match && match[1]) {
        const num = parseFloat(match[1]);
        if (!isNaN(num)) {
          parsedNumberQuery = {
            number: num,
            comparison
          };
          break;
        }
      }
    }
    if (!parsedNumberQuery) {
      const num = parseFloat(trimmedQuery);
      if (!isNaN(num)) {
        parsedNumberQuery = {
          number: num,
          comparison: null
        };
      }
    }
  }
  const parsedNumber = parsedNumberQuery?.number ?? null;
  const looksLikeDateTerm = DATE_TERMS_FOR_SCORING.some(term => term.startsWith(query) || query.startsWith(term));
  const looksLikeDate = tryParseDate(searchQuery) !== null || looksLikeDateTerm;
  const looksLikeNumber = parsedNumber !== null;
  const phoneDigits = trimmedQuery.replace(/\D/g, "");
  const hasPhoneFormatting = /[\s()+-]/.test(trimmedQuery);
  const looksLikePhone = phoneDigits.length >= 3 && /^[\d\s()+-]+$/.test(trimmedQuery);
  const matchesAnyIdPrefix = enhancedFilterTypes.some(ft => {
    if (ft.type !== "id" || !ft.prefix) return false;
    const prefix = ft.prefix.toLowerCase();
    return query.startsWith(prefix) || query.startsWith(prefix + "_") || query.startsWith(prefix + "-");
  });
  return {
    query,
    trimmedQuery,
    parsedNumber,
    parsedNumberQuery,
    providers,
    enhancedFilterTypes,
    looksLikeDate,
    looksLikeNumber,
    looksLikePhone,
    hasPhoneFormatting,
    phoneDigits,
    matchesAnyIdPrefix
  };
}
function computeSearchResults(searchQuery: string, enhancedFilterTypes: FilterTypeConfig[], providers: ReturnType<typeof useProviders>["providers"]): SearchResult[] {
  if (!searchQuery.trim()) return [];
  const ctx = buildSearchContext(searchQuery, enhancedFilterTypes, providers);
  const out: SearchResult[] = [];
  for (const filterType of enhancedFilterTypes) {
    getStrategy(filterType.type).search(filterType, ctx, out);
  }
  const prioritizedKeys = new Set(enhancedFilterTypes.filter(ft => ft.prioritizeInSearch).map(ft => ft.filterKey));
  const rank = (result: SearchResult) => prioritizedKeys.has(result.filterKey) ? 1 : 0;
  return out.sort((a, b) => rank(b) - rank(a) || (b.score ?? 0) - (a.score ?? 0)).slice(0, 30);
}
function computeMatchingFilterTypes(searchQuery: string, enhancedFilterTypes: FilterTypeConfig[]): FilterTypeConfig[] {
  if (!searchQuery.trim()) return [];
  const q = searchQuery.toLowerCase();
  const queryWords = q.split(/\s+/).filter(w => w.length > 0);
  return enhancedFilterTypes.filter(ft => {
    const labelLower = ft.label.toLowerCase();
    return labelLower.includes(q) || queryWords.some(word => labelLower.includes(word));
  });
}
export function QuickApply() {
  const $ = _c(23);
  const {
    searchQuery,
    effectiveFilters
  } = usePanelContext();
  const {
    enhancedFilterTypes
  } = useFiltersContext();
  const {
    updatePending,
    setSearchQuery
  } = useActionsContext();
  const {
    providers
  } = useProviders();
  const deferredQuery = useDeferredValue(searchQuery);
  const hasSearchQuery = deferredQuery.trim().length > 0;
  let t0;
  if ($[0] !== deferredQuery || $[1] !== enhancedFilterTypes || $[2] !== providers) {
    t0 = computeSearchResults(deferredQuery, enhancedFilterTypes, providers);
    $[0] = deferredQuery;
    $[1] = enhancedFilterTypes;
    $[2] = providers;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const searchResults = t0;
  const matchingFilterTypes = computeMatchingFilterTypes(deferredQuery, enhancedFilterTypes);
  if (!hasSearchQuery) {
    return null;
  }
  let t1;
  if ($[4] !== effectiveFilters || $[5] !== enhancedFilterTypes || $[6] !== setSearchQuery || $[7] !== updatePending) {
    t1 = result => {
      if (result.isCustomDate) {
        return;
      }
      const filterConfig = enhancedFilterTypes.find(ft => ft.filterKey === result.filterKey);
      if (!filterConfig) {
        return;
      }
      const {
        value,
        clearSearchQuery
      } = getStrategy(filterConfig.type).toggle(result, effectiveFilters[result.filterKey], filterConfig);
      updatePending(result.filterKey, value);
      if (clearSearchQuery) {
        setSearchQuery("");
      }
    };
    $[4] = effectiveFilters;
    $[5] = enhancedFilterTypes;
    $[6] = setSearchQuery;
    $[7] = updatePending;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const handleToggle = t1;
  let t2;
  if ($[9] !== effectiveFilters || $[10] !== enhancedFilterTypes) {
    t2 = result_0 => {
      const filterConfig_0 = enhancedFilterTypes.find(ft_0 => ft_0.filterKey === result_0.filterKey);
      if (!filterConfig_0) {
        return false;
      }
      return getStrategy(filterConfig_0.type).isSelected(effectiveFilters[result_0.filterKey], result_0.optionValue, result_0.comparison, result_0.numericValue, result_0.datePreset, filterConfig_0);
    };
    $[9] = effectiveFilters;
    $[10] = enhancedFilterTypes;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const isSelected = t2;
  if (matchingFilterTypes.length === 0 && searchResults.length === 0) {
    let t3;
    if ($[12] !== deferredQuery) {
      t3 = <div className="px-3 py-2 text-body text-muted-foreground">No results for “{deferredQuery}”</div>;
      $[12] = deferredQuery;
      $[13] = t3;
    } else {
      t3 = $[13];
    }
    return t3;
  }
  let t3;
  if ($[14] !== handleToggle || $[15] !== isSelected || $[16] !== searchResults) {
    let t4;
    if ($[18] !== handleToggle || $[19] !== isSelected) {
      t4 = (result_1, index) => {
        const FilterIcon = getFilterIcon(result_1.type, result_1.filterKey);
        const selected = isSelected(result_1);
        const isFuzzySearch = typeof result_1.optionLabel === "string" && result_1.optionLabel.startsWith("~");
        return <DropdownMenuItem key={`${result_1.filterKey}-${result_1.optionValue}-${result_1.comparison || ""}-${index}`} onSelect={e => {
          if (result_1.type === "multiselect" || result_1.type === "location") {
            e.preventDefault();
          }
          handleToggle(result_1);
        }} className={cx("flex w-full items-center gap-1.5 rounded-lg px-3 py-1.5 text-left text-body", selected && "bg-primary-50/50 dark:bg-primary-900/30")}>{selected ? <RiCheckLine className="h-4 w-4 text-primary" /> : <FilterIcon className="h-4 w-4 text-foreground-muted" />}<span className="text-muted-foreground">{result_1.filterLabel}</span>{result_1.indicator ? <span className="text-primary">{result_1.indicator}</span> : null}<span className="font-medium text-primary">{isFuzzySearch ? result_1.optionLabel.replace(/^~\s*/, "") : result_1.optionLabel}</span></DropdownMenuItem>;
      };
      $[18] = handleToggle;
      $[19] = isSelected;
      $[20] = t4;
    } else {
      t4 = $[20];
    }
    t3 = searchResults.map(t4);
    $[14] = handleToggle;
    $[15] = isSelected;
    $[16] = searchResults;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  let t4;
  if ($[21] !== t3) {
    t4 = <>{t3}</>;
    $[21] = t3;
    $[22] = t4;
  } else {
    t4 = $[22];
  }
  return t4;
}